import React, { useEffect } from "react";
import { useBlocker } from "react-router-dom";
import { useTranslation } from "i18n";
import { ITrackableChange } from "services/changeTrackingRegistryService";

export default function withUnsavedChangesNavigationPrevention(
  window: {
    onbeforeunload:
      | ((this: WindowEventHandlers, ev: BeforeUnloadEvent) => any)
      | null;
  },
  changeTrackableGetter: () => ITrackableChange | undefined
) {
  return function withNavigationPreventionHocFactory<
    OriginalProps extends object,
  >(
    WrappedComponent: React.ComponentType<
      React.PropsWithChildren<OriginalProps>
    >
  ) {
    return function ComponentWithNavigationPrevention(props: OriginalProps) {
      const { t } = useTranslation();

      const blocker = useBlocker(({ currentLocation, nextLocation }) => {
        if (currentLocation.pathname === nextLocation.pathname) return false;

        const navigationPrevention = changeTrackableGetter();

        return !!navigationPrevention?.isUnsaved();
      });

      useEffect(() => {
        if (blocker.state === "blocked") {
          const blockingMessage =
            changeTrackableGetter()?.unsavedChangesMessage() ??
            t("common:confirmationPopUp.title.unsavedChanges");

          if (confirm(blockingMessage)) {
            blocker.proceed();
          } else {
            blocker.reset();
          }
        }
      }, [blocker]);

      useEffect(() => {
        window.onbeforeunload = (event: BeforeUnloadEvent) => {
          const navigationPrevention = changeTrackableGetter();

          if (navigationPrevention?.isUnsaved()) {
            const blockingMessage =
              navigationPrevention.unsavedChangesMessage();

            if (blockingMessage) {
              event.preventDefault();
              event.returnValue = blockingMessage;

              return blockingMessage;
            }
          }

          return null;
        };

        return () => {
          window.onbeforeunload = null;
        };
      }, []);

      return <WrappedComponent {...props} />;
    };
  };
}
